import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Sparkles, ClipboardCheck, Calendar, Clock, TrendingUp, Briefcase, Building2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/useAuth';
import { supabase } from '@core/supabase/client';
import { useCommandPalette } from '@shared/components/ui/CommandPaletteProvider';
import { useHomeQueues } from '@modules/dashboard/hooks/useHomeQueues';
import { StatusChip } from '@shared/components/ui/StatusChip';
import { REQUISITION_STATUS_LABELS } from '@core/constants/requisitionLifecycle';
import type { RequisitionStatus } from '@core/enums';

const IS_MAC =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);

const QUICK_ROLES = [
  'Software Engineer',
  'Product Designer',
  'Marketing Lead',
  'Data Scientist',
] as const;

function getFirstName(name?: string | null, email?: string | null): string {
  const full = (name ?? '').trim();
  if (full) return full.split(/\s+/)[0];
  return (email ?? '').split('@')[0] || 'there';
}

function timeAgo(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const REQ_STATUS_DOT: Record<RequisitionStatus, string> = {
  sourcing: '#10B981',
  approved: '#10B981',
  filled: '#10B981',
  submitted: '#3B82F6',
  awaiting_client: '#F59E0B',
  on_hold: '#9CA3AF',
  draft: '#9CA3AF',
  rejected: '#EF4444',
  cancelled: '#EF4444',
};

type RecentReq = {
  id: string;
  title: string;
  status: RequisitionStatus;
  updated_at: string;
  headcount: number;
  client: { id: string; name: string } | null;
};

export function HomePage() {
  const { user } = useAuth();
  const { openNewSearch } = useCommandPalette();
  const navigate = useNavigate();

  const orgId = user?.orgId ?? undefined;
  const orgUserId = user?.orgUserId ?? undefined;
  const firstName = getFirstName(user?.name, user?.email);

  const { needsReview, interviewsToday, awaitingMe, newHighMatches } = useHomeQueues(
    orgId,
    orgUserId,
  );

  const recentReqsQuery = useQuery({
    queryKey: ['home', 'recentReqs', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ggj_b2b_requisitions')
        .select('id, title, status, updated_at, headcount, client:ggj_b2b_clients(id, name)')
        .eq('org_id', orgId!)
        .order('updated_at', { ascending: false })
        .limit(6);
      if (error) throw error;
      return (data ?? []) as RecentReq[];
    },
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });

  const recentReqs = recentReqsQuery.data ?? [];

  const KPI_CARDS = [
    {
      label: 'Needs review',
      count: needsReview.data?.totalListed ?? null,
      loading: needsReview.isLoading,
      to: '/applications',
      icon: ClipboardCheck,
      iconBg: 'rgba(239, 68, 68, 0.15)',
      iconColor: '#EF4444',
    },
    {
      label: 'Interviews today',
      count: interviewsToday.data?.totalListed ?? null,
      loading: interviewsToday.isLoading,
      to: '/interviews',
      icon: Calendar,
      iconBg: 'rgba(59, 130, 246, 0.15)',
      iconColor: '#3B82F6',
    },
    {
      label: 'Awaiting you',
      count: awaitingMe.data?.totalListed ?? null,
      loading: awaitingMe.isLoading,
      to: '/applications',
      icon: Clock,
      iconBg: 'rgba(245, 158, 11, 0.15)',
      iconColor: '#F59E0B',
    },
    {
      label: 'New high matches',
      count: newHighMatches.data?.totalListed ?? null,
      loading: newHighMatches.isLoading,
      to: '/smart-match',
      icon: TrendingUp,
      iconBg: 'rgba(16, 185, 129, 0.15)',
      iconColor: '#10B981',
    },
  ] as const;

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4">

      {/* ── 1. Hero section ─────────────────────────────────────────────── */}
      <div className="mt-8 mb-10 rounded-2xl border vscode-border overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(59, 130, 246, 0.05) 100%)' }}>
        <div className="px-8 py-10">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="text-sm vscode-text-tertiary mb-2">Welcome back</p>
              <h1 className="text-2xl md:text-3xl font-bold vscode-text-primary mb-3">
                Hey {firstName}, who can I help you find?
              </h1>
              <p className="text-sm vscode-text-secondary max-w-md">
                Search for candidates, review applications, or start a new talent search.
              </p>
            </div>
            <button
              type="button"
              onClick={() => openNewSearch()}
              className="flex items-center gap-2.5 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
            >
              <Sparkles className="h-4 w-4" aria-hidden />
              Start a search
              <kbd className="ml-1 rounded-md border border-white/30 bg-white/10 px-1.5 py-0.5 text-[11px] font-medium">
                {IS_MAC ? '⌘K' : 'Ctrl+K'}
              </kbd>
            </button>
          </div>

          {/* Quick-role chips */}
          <div className="mt-8 flex flex-wrap gap-2">
            {QUICK_ROLES.map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => openNewSearch(role)}
                className="flex items-center gap-2 rounded-full border vscode-border vscode-bg-secondary px-4 py-2 text-sm vscode-text-secondary transition-all hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400"
              >
                <Search className="h-3.5 w-3.5" />
                {role}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── 2. YOUR WORK TODAY ──────────────────────────────────────── */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 rounded-full bg-emerald-500" />
          <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
            Your work today
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {KPI_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.label}
                type="button"
                onClick={() => navigate(card.to)}
                className="group relative flex flex-col rounded-xl border vscode-border vscode-bg-secondary p-5 text-left transition-all hover:border-emerald-500/50 hover:shadow-lg"
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{ background: card.iconBg }}
                  >
                    <Icon className="h-5 w-5" style={{ color: card.iconColor }} />
                  </div>
                  <ArrowRight
                    className="h-4 w-4 vscode-text-tertiary opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-1"
                    style={{ color: card.iconColor }}
                    aria-hidden
                  />
                </div>
                {card.loading ? (
                  <div className="mb-1 h-9 w-12 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
                ) : (
                  <span
                    className="block text-3xl font-bold leading-none"
                    style={{ color: (card.count ?? 0) > 0 ? card.iconColor : 'var(--vscode-text-tertiary)' }}
                  >
                    {card.count ?? '—'}
                  </span>
                )}
                <span className="mt-2 block text-sm vscode-text-secondary">{card.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── 3. Recent searches ──────────────────────────────────────── */}
      <section className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 rounded-full bg-blue-500" />
          <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
            Recent searches
          </p>
        </div>

        {recentReqsQuery.isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[140px] animate-pulse rounded-xl vscode-bg-tertiary"
              />
            ))}
          </div>
        ) : recentReqs.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border vscode-border vscode-bg-secondary py-12 text-center">
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
              <Search className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-base font-medium vscode-text-primary mb-1">
                No searches yet
              </p>
              <p className="text-sm vscode-text-tertiary">
                Start your first talent search to see it here.
              </p>
            </div>
            <button
              type="button"
              onClick={() => openNewSearch()}
              className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-all hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
            >
              Start a search
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {recentReqs.map((req) => (
              <button
                key={req.id}
                type="button"
                onClick={() => navigate(`/requisitions/${req.id}`)}
                className="group flex flex-col rounded-xl border vscode-border vscode-bg-secondary p-4 text-left transition-all hover:border-emerald-500/50 hover:shadow-lg"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.15)' }}>
                    <Briefcase className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold vscode-text-primary group-hover:text-emerald-500 transition-colors">
                      {req.title}
                    </span>
                    {req.client?.name ? (
                      <span className="flex items-center gap-1 mt-0.5 text-xs vscode-text-tertiary">
                        <Building2 className="h-3 w-3" />
                        <span className="truncate">{req.client.name}</span>
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-auto pt-3 border-t vscode-border">
                  <StatusChip
                    label={REQUISITION_STATUS_LABELS[req.status] ?? req.status}
                    dotColor={REQ_STATUS_DOT[req.status] ?? '#9CA3AF'}
                  />
                  <span className="text-[11px] vscode-text-tertiary">
                    {timeAgo(req.updated_at)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

    </div>
  );
}

export default HomePage;
